import type { Paragraph, Table as DocxTable } from "docx";
import { downloadWordDocument } from "@/lib/documentGenerator/exportHistory";
import { createStampForCurrentUser } from "@/lib/documentGenerator/watermark";
import { body, buildDocument, h1, h2, factTable, table, fmtDate } from "@/lib/documentGenerator/docTheme";
import { documentFooter } from "@/lib/documentGenerator/documentFooter";
import {
  BYGGHERRE_HANDLING_SECTIONS,
  DELEGATION_ORDER_SECTIONS,
  BYGGHERRE_STATUS_LABELS,
  BYGGHERRE_CHECKLIST_PHASES,
  BYGGHERRE_CHECKLIST_STATUS_LABELS,
  DELEGATION_LETTER_STATUS_LABELS,
  DELEGATION_LETTER_TEMPLATE,
  DUTY_ROLE_CODES,
} from "@/lib/basProcess/byggherreDocs";

const safe = (name?: string) => (name || "projekt").replace(/[^\w-]+/g, "_");

interface SectionDoc {
  name?: string;
  version?: string;
  status?: string;
  sections?: Record<string, string>;
}

/** Dok 50 – Byggherrens hantering av byggarbetsmiljöarbete */
export const generateByggherreHandlingWordDocument = async (
  doc0: SectionDoc,
  projectName?: string
) => {
  const documentName = doc0.name || "Byggherrens hantering av byggarbetsmiljöarbete";
  const stamp = await createStampForCurrentUser({ docNumber: "50", documentName, projectName });
  const footer = await documentFooter({ docNumber: "50", documentType: "byggherre_hantering" });

  const children: (Paragraph | DocxTable)[] = BYGGHERRE_HANDLING_SECTIONS.flatMap((s, i) => [
    h1(`${i + 1}. ${s.title}`),
    body(doc0.sections?.[s.key] ?? ""),
  ]);

  const doc = buildDocument({
    meta: {
      review: footer.review,
      docNumber: "50",
      documentName,
      projectName,
      version: doc0.version || "1.0",
      status: BYGGHERRE_STATUS_LABELS[doc0.status || ""] || doc0.status,
    },
    stamp,
    children: [...children, ...footer.children],
  });

  await downloadWordDocument(doc, `Dok50_Byggherrens_hantering_${safe(projectName)}.docx`, {
    documentType: "byggherre_hantering",
    docNumber: "50",
    documentName,
    snapshot: doc0,
    stamp,
  });
};

/** Dok 51 – Delegeringsordning byggherre */
export const generateDelegationOrderWordDocument = async (
  doc0: SectionDoc,
  projectName?: string
) => {
  const documentName = doc0.name || "Delegeringsordning byggherre";
  const stamp = await createStampForCurrentUser({ docNumber: "51", documentName, projectName });
  const footer = await documentFooter({ docNumber: "51", documentType: "delegeringsordning" });

  const children: (Paragraph | DocxTable)[] = DELEGATION_ORDER_SECTIONS.flatMap((s, i) => [
    h1(`${i + 1}. ${s.title}`),
    body(doc0.sections?.[s.key] ?? ""),
  ]);

  const doc = buildDocument({
    meta: {
      review: footer.review,
      docNumber: "51",
      documentName,
      projectName,
      version: doc0.version || "1.0",
      status: BYGGHERRE_STATUS_LABELS[doc0.status || ""] || doc0.status,
    },
    stamp,
    children: [...children, ...footer.children],
  });

  await downloadWordDocument(doc, `Dok51_Delegeringsordning_${safe(projectName)}.docx`, {
    documentType: "delegeringsordning",
    docNumber: "51",
    documentName,
    snapshot: doc0,
    stamp,
  });
};

export interface DelegationLetterRow {
  id?: string;
  from_name?: string | null;
  from_role?: string | null;
  to_name?: string | null;
  to_role?: string | null;
  scope?: string | null;
  authority?: string | null;
  competence_note?: string | null;
  may_delegate_further?: boolean;
  deputy_name?: string | null;
  valid_from?: string | null;
  valid_to?: string | null;
  signed_at?: string | null;
  status: string;
  note?: string | null;
}

/** Dok 52 – Delegeringsbrev (ett brev per mottagare) */
export const generateDelegationLetterWordDocument = async (
  letter: DelegationLetterRow,
  projectName?: string
) => {
  const documentName = `Delegeringsbrev – ${letter.to_name || "mottagare"}`;
  const stamp = await createStampForCurrentUser({ docNumber: "52", documentName, projectName });
  const footer = await documentFooter({ docNumber: "52", documentType: "delegeringsbrev" });

  const children: (Paragraph | DocxTable)[] = [
    h1("Delegeringsbrev – byggherrens arbetsmiljöansvar"),
    factTable([
      ["Fördelas av", [letter.from_name, letter.from_role].filter(Boolean).join(", ") || "–"],
      ["Fördelas till", [letter.to_name, letter.to_role].filter(Boolean).join(", ") || "–"],
      ["Gäller från", fmtDate(letter.valid_from)],
      ["Gäller till", letter.valid_to ? fmtDate(letter.valid_to) : "Tills vidare"],
      ["Undertecknat", fmtDate(letter.signed_at)],
      ["Får fördela vidare", letter.may_delegate_further ? "Ja" : "Nej – får dock låta annan handlägga"],
      ["Ställföreträdare", letter.deputy_name || "–"],
      ["Status", DELEGATION_LETTER_STATUS_LABELS[letter.status] || letter.status],
    ]),
    h2("Bakgrund"),
    body(DELEGATION_LETTER_TEMPLATE.background),
    h2("Omfattning"),
    body(letter.scope || DELEGATION_LETTER_TEMPLATE.scope),
    h2("Utbildning och kompetens"),
    body(letter.competence_note || DELEGATION_LETTER_TEMPLATE.competence),
    h2("Befogenheter"),
    body(letter.authority || DELEGATION_LETTER_TEMPLATE.authority),
    h2("Returnering av arbetsuppgift"),
    body(DELEGATION_LETTER_TEMPLATE.returnClause),
    h2("Underskrifter"),
    table(
      ["", "Namn", "Roll", "Datum", "Underskrift"],
      [
        ["Fördelar", letter.from_name, letter.from_role, fmtDate(letter.signed_at), ""],
        ["Mottagare", letter.to_name, letter.to_role, fmtDate(letter.signed_at), ""],
      ]
    ),
  ];

  if (letter.note) {
    children.push(h2("Kommentar"), body(letter.note));
  }

  const doc = buildDocument({
    meta: {
      review: footer.review,
      docNumber: "52",
      documentName,
      projectName,
      status: DELEGATION_LETTER_STATUS_LABELS[letter.status] || letter.status,
    },
    stamp,
    children: [...children, ...footer.children],
  });

  await downloadWordDocument(
    doc,
    `Dok52_Delegeringsbrev_${safe(letter.to_name || "")}_${safe(projectName)}.docx`,
    {
      documentType: "delegeringsbrev",
      docNumber: "52",
      documentName,
      snapshot: letter,
      stamp,
    }
  );
};

export interface ByggherreChecklistRow {
  phase: string;
  activity: string;
  requirement?: string | null;
  question?: string | null;
  method?: string | null;
  responsible?: string | null;
  status: string;
  note?: string | null;
  due_date?: string | null;
}

/** Dok 53 – Checklista lagefterlevnad byggherre */
export const generateByggherreChecklistWordDocument = async (
  items: ByggherreChecklistRow[],
  projectName?: string
) => {
  const documentName = "Checklista – lagefterlevnad byggherre";
  const stamp = await createStampForCurrentUser({ docNumber: "53", documentName, projectName });
  const footer = await documentFooter({ docNumber: "53", documentType: "byggherre_checklista" });

  const intro: (Paragraph | DocxTable)[] = [
    body(
      "Checklistan visar hur byggherren utövar tillsyn och kontroll i varje skede enligt AFS 2023:3 kap. 2. Status anges som OK, EJ OK eller N/A. N/A kräver en motivering i kommentarsfältet."
    ),
  ];

  const children: (Paragraph | DocxTable)[] = BYGGHERRE_CHECKLIST_PHASES.flatMap((phase) => {
    const rows = items.filter((i) => i.phase === phase.key);
    if (!rows.length) return [];
    return [
      h1(phase.label),
      table(
        ["Aktivitet", "Krav", "Frågeställning", "Frekvens, metod, verktyg", "Ansvarig", "Status", "Kommentar"],
        rows.map((r) => [
          r.activity,
          r.requirement,
          r.question,
          r.method,
          r.responsible,
          BYGGHERRE_CHECKLIST_STATUS_LABELS[r.status] || r.status,
          [r.note, r.due_date ? `Senast ${fmtDate(r.due_date)}` : null].filter(Boolean).join(" · "),
        ])
      ),
    ];
  });

  const doc = buildDocument({
    meta: { review: footer.review, docNumber: "53", documentName, projectName },
    stamp,
    children: [...intro, ...children, ...footer.children],
  });

  await downloadWordDocument(doc, `Dok53_Checklista_byggherre_${safe(projectName)}.docx`, {
    documentType: "byggherre_checklista",
    docNumber: "53",
    documentName,
    snapshot: items,
    stamp,
  });
};

export interface DutyAssignmentRow {
  task: string;
  representative: string;
  handler: string;
  basp: string;
  basu: string;
  note?: string | null;
}

/** Dok 54 – Fördelning av arbetsmiljöuppgifter */
export const generateDutyAssignmentWordDocument = async (
  rows: DutyAssignmentRow[],
  projectName?: string
) => {
  const documentName = "Fördelning av arbetsmiljöuppgifter";
  const stamp = await createStampForCurrentUser({ docNumber: "54", documentName, projectName });
  const footer = await documentFooter({ docNumber: "54", documentType: "uppgiftsfordelning" });

  const children: (Paragraph | DocxTable)[] = [
    body(
      "Tabellen tydliggör fördelningen av arbetsmiljöuppgifter mellan byggherrens representant, handläggare och byggarbetsmiljösamordnarna. Används särskilt i underhålls- och avhjälpandeprojekt."
    ),
    h1("Teckenförklaring"),
    table(
      ["Kod", "Betydelse"],
      Object.entries(DUTY_ROLE_CODES).map(([code, label]) => [code, label])
    ),
    h1("Fördelning av arbetsuppgifter"),
    table(
      ["Arbetsuppgift", "Representant", "Handläggare", "Bas-P", "Bas-U", "Kommentar"],
      rows.map((r) => [r.task, r.representative, r.handler, r.basp, r.basu, r.note])
    ),
  ];

  const doc = buildDocument({
    meta: { review: footer.review, docNumber: "54", documentName, projectName },
    stamp,
    children: [...children, ...footer.children],
  });

  await downloadWordDocument(doc, `Dok54_Uppgiftsfordelning_${safe(projectName)}.docx`, {
    documentType: "uppgiftsfordelning",
    docNumber: "54",
    documentName,
    snapshot: rows,
    stamp,
  });
};
